import { PaginationQueryObject, QueryConfig } from "./Queries";
import { UserRequest, User } from "../../routeComponents/UserManagement/User";
import { UserPreference } from "../../routeComponents/UserManagement/UserPreference";

export const usersListQuery = (
  filter: any,
  pagination: PaginationQueryObject
): QueryConfig => {
  const query = `query getUsersList($filter: UserFilter, $pagination: PaginationInput){
            users(filter: $filter, paginationInput: $pagination){
                items { 
                  id,
                  displayedLoginName,
                  active,
                  userDemographic { 
                    firstName,
                    lastName,
                    email,
                    displayName
                  }
                },
                pageInfo { 
                  pageSize,
                  pageNumber,
                  totalElements,
                  totalPages,
                  hasNext,
                  hasPrevious }
            }
        }`;
  return {
    query: query,
    variables: {
      filter,
      pagination,
    },
  };
};

export const getUserQuery = (userId: string): QueryConfig => {
  const query = `query getUser($userId: ID) {
    user(id: $userId) {
      id
      displayedLoginName
      actualLoginName
      active
      userDemographic {
        id
        firstName
        lastName
        email
        phone
        birthDate
        gender
      }
      customer {
        id
      }
      userPreferences {
        userId
        key
        value
      }
      person {
        id
        relationshipToCustomer
        primaryLocation
        yearsInJob
        personSensitive {
          gender
          birthDate
          streetAddress
          address2
          city
          zipPostal
          stateProv
          country
          currentPosition
          currentPositionStartDate
        }
      }
    }
  }
  `;
  return {
    query: query,
    variables: {
      userId: userId,
    },
  };
};

export const getUserWithPermissionFlagsQuery = (
  userId: string,
  customerId?: string
): QueryConfig => {
  const query = `query getUser($userId: ID, $customerId: ID) {
            user(id: $userId) {
                id, displayedLoginName, actualLoginName,
                active,
                userDemographic {
                    firstName, lastName, email, phone, birthDate, gender
                },
                customer { id },
                permissions { 
                    accelerate(customerId: $customerId) {
                        canViewUsers, canEditUsers, canViewMyProfile, canViewCustomers, canEditCustomers
                        canViewAppConfig, canEditAppConfig, canDeleteAppConfig, canEditSSO
                        canDeleteSSO, canViewSSO, canViewRole, canViewPolicy
                        canEditRole, canAssignRole, canAssignApplications
                    } 
                }
            } 
        }`;
  return {
    query: query,
    variables: {
      userId: userId,
      customerId: customerId,
    },
  };
};

export const updateUserMutation = (user: User): QueryConfig => {
  const query = `mutation handleUpdateUser($userId: ID!, $username: String, $firstName: String!, $lastName: String!, $email: String, $phone: String) { 
            updateUserAndDemographic (
                input:{
                    userId: $userId,
                    username: $username,
                    firstName: $firstName,
                    lastName: $lastName,
                    email: $email,
                    phone: $phone
                }
            ) { id }
        }`;
  return {
    query: query,
    variables: {
      userId: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      username: user.displayedLoginName,
      phone: user.phone,
    },
  };
};

export const createUserMutation = (
  customerId: string,
  user: UserRequest
): QueryConfig => {
  const query = `mutation handleCreateUser($customerId: ID!, $firstName: String!, $lastName: String!, $email: String!, $username: String!, $phone: String) {
            createUserWithDemographic (
                input: {
                    customerId: $customerId,
                    firstName: $firstName,
                    lastName: $lastName,
                    email: $email,
                    username: $username,
                    phone: $phone
                }
            ) { id }
        }`;
  return {
    query: query,
    variables: {
      customerId: customerId,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      username: user.displayedLoginName,
      phone: user.phone,
    },
  };
};

export const updateUserStatusMutation = (
  newStatus: boolean,
  userId: string
): QueryConfig => {
  const query = `mutation handleUpdateUserStatus($input: UserUpdateInput) {
            updateUser(input: $input) { id, active }
        }`;
  return {
    query: query,
    variables: {
      input: {
        active: newStatus,
        id: userId,
      },
    },
  };
};

export const saveUserPreferredMutation = (
  userPreferences: UserPreference[]
): QueryConfig => {
  const query = `mutation handleSaveUserPreferred($userPreferences: [UserPreferencesInput]) {
            saveUserPreferred(input: $userPreferences) { userId, key, value }
        }`;
  return {
    query: query,
    variables: {
      userPreferences: userPreferences,
    },
  };
};
